export interface OperationalControlSettings {
  greenThreshold: number;
  yellowThreshold: number;
  orangeThreshold: number;
  redThreshold: number;
  kpiWeight: number;
  planWeight: number;
  weightComisiones: number;
  weightComites: number;
  weightConsejerias: number;
  stale1Months: number;
  stale2Months: number;
  stale3Months: number;
  stalePenalty: number;
  stalePenaltyEnabled: boolean;
  quarterlyHardeningEnabled: boolean;
  staleWarningMonths: number;
  staleCriticalMonths: number;
}

export const DEFAULT_OPERATIONAL_CONTROL_SETTINGS: OperationalControlSettings = {
  greenThreshold: 90,
  yellowThreshold: 75,
  orangeThreshold: 60,
  redThreshold: 60,
  kpiWeight: 70,
  planWeight: 30,
  weightComisiones: 50,
  weightComites: 30,
  weightConsejerias: 20,
  stale1Months: 1,
  stale2Months: 2,
  stale3Months: 3,
  stalePenalty: 10,
  stalePenaltyEnabled: true,
  quarterlyHardeningEnabled: false,
  staleWarningMonths: 2,
  staleCriticalMonths: 3
};

export const LOCAL_STORAGE_KEY = 'gov_operational_control_settings';

export const loadOperationalSettings = (): OperationalControlSettings => {
  if (typeof window === 'undefined') return DEFAULT_OPERATIONAL_CONTROL_SETTINGS;
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const stale2 = Number(parsed.stale2Months ?? 2);
      const stale3 = Number(parsed.stale3Months ?? 3);
      return {
        greenThreshold: Number(parsed.greenThreshold ?? 90),
        yellowThreshold: Number(parsed.yellowThreshold ?? 75),
        orangeThreshold: Number(parsed.orangeThreshold ?? 60),
        redThreshold: Number(parsed.redThreshold ?? 60),
        kpiWeight: Number(parsed.kpiWeight ?? 70),
        planWeight: Number(parsed.planWeight ?? 30),
        weightComisiones: Number(parsed.weightComisiones ?? 50),
        weightComites: Number(parsed.weightComites ?? 30),
        weightConsejerias: Number(parsed.weightConsejerias ?? 20),
        stale1Months: Number(parsed.stale1Months ?? 1),
        stale2Months: stale2,
        stale3Months: stale3,
        stalePenalty: Number(parsed.stalePenalty ?? 10),
        stalePenaltyEnabled: Boolean(parsed.stalePenaltyEnabled ?? true),
        quarterlyHardeningEnabled: Boolean(parsed.quarterlyHardeningEnabled ?? false),
        staleWarningMonths: stale2,
        staleCriticalMonths: stale3
      };
    }
  } catch (e) {
    console.error('Error loading settings', e);
  }
  return DEFAULT_OPERATIONAL_CONTROL_SETTINGS;
};

export const saveOperationalSettings = (settings: OperationalControlSettings): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    if (settings.kpiWeight + settings.planWeight !== 100) {
      return false;
    }
    const cleanSettings = {
      ...settings,
      staleWarningMonths: settings.stale2Months,
      staleCriticalMonths: settings.stale3Months
    };
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(cleanSettings));
    return true;
  } catch (e) {
    console.error('Error saving settings', e);
    return false;
  }
};

/**
 * Obtiene los umbrales de semaforización ajustados por trimestre si está habilitado el endurecimiento.
 */
export const getActiveOperationalSettings = (
  selectedMonthIndex: number,
  settings: OperationalControlSettings = loadOperationalSettings()
): OperationalControlSettings => {
  if (!settings.quarterlyHardeningEnabled) return settings;

  const quarter = Math.min(3, Math.max(0, Math.floor(selectedMonthIndex / 3)));
  const offsets = [0, 2, 4, 5];
  const offset = offsets[quarter];

  return {
    ...settings,
    greenThreshold: Math.min(100, settings.greenThreshold + offset),
    yellowThreshold: Math.min(100, settings.yellowThreshold + offset),
    orangeThreshold: Math.min(100, settings.orangeThreshold + offset)
  };
};
